import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Avatar, Button, Divider, FormControl, Grid, InputLabel, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import handleTicketAccessToUser from "../../utils/validations/handleTicketAccessToUser";
import toastError from "../../errors/toastError";
import TooltipContainer from "../TooltipContainer";
import useWhatsApps from "../../hooks/useWhatsApps";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import JidUtils from "../../utils/classes/JidUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles((theme) => ({
  vCardContainer: {
    minWidth: "250px",
  },

  vCardInfo: {
    color: theme.palette.secondary.ackCheck,
    userSelect: "none",
    marginTop: "2.50%",
    marginLeft: "10px",
    "&:hover": { textDecoration: "underline" },
  },
}));

const VcardPreview = ({ contact, number, enableHandleNewChat=true }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { whatsApps: whatsAppsList } = useWhatsApps();

  const [selectedContact, setContact] = useState({ name: "", number: "", profilePicUrl: "" });
  const [selectedWhatsappId, setSelectedWhatsappId] = useState(null);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);

  const [openPrivateChatConversationModal, setOpenPrivateChatConversationModal] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (user.whatsapp?.id) setSelectedWhatsappId(user.whatsapp.id);
    else if (whatsAppsList.length === 1) setSelectedWhatsappId(whatsAppsList[0].id);
  }, [user, whatsAppsList]);
  
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          if (!number) {
            setContact({ name: contact, number: i18n.t("vCard.phoneNumberNotAvailable"), profilePicUrl: "" });
            setIsPhoneNumberValid(false);
            return;
          }

          const contactObj = { name: contact, number: JidUtils.formatJidToVCards(number), email: "" };
          const { data } = await api.post("/contactVCard", contactObj);

          setIsPhoneNumberValid(data.success);

          if (data.success) setContact(data.contact);
          else toast.info(i18n.t(data.message));
        }
        catch (exception) {
          console.log("VCard Preview Use Effect 1 Exception:", exception);
          toastError(exception);
        }
      };

      fetchContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [contact, number]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleOpenPrivateChatConversationModal = (selectedContactNumber, enableHandleNewChat) => {
    if (!selectedContactNumber || !enableHandleNewChat || !isPhoneNumberValid) return;
    setOpenPrivateChatConversationModal(true);
  };
  
  const handleClosePrivateChatConversationModal = () => {
    setOpenPrivateChatConversationModal(false);
    setSelectedWhatsappId(null);
  };

  const handleNewChat = async () => {
    if (!selectedWhatsappId) {
      toast.info(i18n.t("newTicketModal.toasts.whatsappNotSelected"));
      return;
    }

    if (!isPhoneNumberValid) {
      toast.info(i18n.t("vCard.info"));
      return;
    }

    await handleTicketAccessToUser(
      selectedContact.id,
      user.id,
      user.profile,
      "open",
      true,
      selectedWhatsappId,
      null,
      (ticketId, ticketStatus) => {
        localStorage.setItem("tabOpenTickets", ticketStatus);
        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticketId}`);
        setShouldSyncRouteOnMenu(true);
      }
    );
  }



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.vCardContainer}>
      <ConfirmationModal
        title={`${i18n.t("vCard.confirmationModal.title")} ${selectedContact.name}?`}
        open={openPrivateChatConversationModal}
        onClose={handleClosePrivateChatConversationModal}
        onConfirm={async () => await handleNewChat()}
      >
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("newTicketModal.connection")}</InputLabel>
          <Select
            value={selectedWhatsappId}
            onChange={(event) => setSelectedWhatsappId(event.target.value)}
            label={i18n.t("newTicketModal.connection")}
            fullWidth
          >
            <MenuItem value={""}>&nbsp;</MenuItem>
            {whatsAppsList.map((whatsapp) => (
              <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </ConfirmationModal>

      <Grid container spacing={0}>
        <Grid item xs={2}>
          <Avatar src={selectedContact.profilePicUrl} />
        </Grid>

        <Grid item xs={9}>
          <Typography
            onClick={() => handleOpenPrivateChatConversationModal(selectedContact.number, enableHandleNewChat)}
            className={classes.vCardInfo}
            style={{ cursor: isPhoneNumberValid && enableHandleNewChat ? "pointer" : "default" }}
            variant="subtitle1"
            gutterBottom
          >
            {selectedContact.name}

            {!isPhoneNumberValid && (
              <TooltipContainer i18nMessage={"backendErrors.ERR_NUMBER_NOT_IN_WHATSAPP"} />
            )}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Divider />

          <Button
            fullWidth
            color="inherit"
            onClick={() => handleOpenPrivateChatConversationModal(selectedContact.number, enableHandleNewChat)}
            disabled={!isPhoneNumberValid || !enableHandleNewChat}
            style={{ cursor: isPhoneNumberValid && enableHandleNewChat ? "pointer" : "default" }}
          >
            {i18n.t("vCard.button")}
          </Button>
        </Grid>
      </Grid>
    </div>
);

};

export default VcardPreview;